/**
 * Matching an article's outline to the text of its body.
 *
 * The editor holds one Markdown document; the plan holds a list of sections. As
 * long as the two agree, the rail beside the editor can do useful things: jump
 * to a section, count its characters against its target, redraw it on its own.
 * The moment somebody edits freely - renames a heading, writes a new one, moves
 * a paragraph across a boundary - they stop agreeing, and every one of those
 * actions becomes a way to destroy work.
 *
 * So the question this module answers is not "where is section three" but "do
 * these two still describe the same article". `locate` returns null the instant
 * they do not, and the rail degrades to a read-only map parsed from the
 * document's own headings. That is the whole design decision: the sections lead
 * while a draft is being assembled, the text leads once a person has taken over,
 * and nothing guesses which one is in charge.
 *
 * Plain functions with no React in sight, because the interesting part is the
 * matching and it should be testable without a browser.
 */

import { visibleLength } from '../../shared/rich-format.ts';

/** Where one section's text sits in the document. */
export interface SectionRange {
  /** Index of the first character of the heading line. */
  start: number;
  /** Index one past the section's last character - the next heading, or the end. */
  end: number;
}

/** A heading found in the document itself. */
export interface FoundHeading {
  level: number;
  text: string;
  start: number;
  end: number;
}

/**
 * Every ATX heading in the document, in order.
 *
 * Used for the read-only map when the outline no longer matches, and to find the
 * boundaries of a section that does. Fenced code blocks are skipped: a `#` at the
 * start of a line inside a fence is a comment in somebody's example, not a
 * heading, and treating it as one splits an article in a place it does not have.
 */
export function parseHeadings(body: string): FoundHeading[] {
  const found: FoundHeading[] = [];
  let at = 0;
  let fenced = false;

  for (const line of body.split('\n')) {
    const start = at;
    at += line.length + 1;

    if (/^\s*(```|~~~)/.test(line)) {
      fenced = !fenced;
      continue;
    }
    if (fenced) continue;

    const match = /^(#{1,6})\s+(.*\S)\s*$/.exec(line);
    if (!match) continue;
    found.push({
      level: match[1]!.length,
      text: match[2]!,
      start,
      end: start + line.length,
    });
  }

  // Each heading owns the text up to the next one; the last owns the remainder.
  return found.map((heading, index) => ({
    ...heading,
    end: found[index + 1]?.start ?? body.length,
  }));
}

/**
 * Where each planned section sits in the body, or null if the two disagree.
 *
 * Matched by heading text, in order, and every section must be found - a partial
 * match is a disagreement, not a partial success. Two sections may legitimately
 * share a heading, so the search continues from where the previous one ended
 * rather than scanning the whole document each time.
 *
 * Deliberately strict. The alternative - fuzzy matching, or taking the nth
 * heading regardless of its text - is how a redraw lands in the wrong section
 * and overwrites a paragraph somebody wrote by hand.
 */
export function locate(body: string, headings: readonly string[]): SectionRange[] | null {
  if (headings.length === 0) return null;
  const found = parseHeadings(body);
  const ranges: SectionRange[] = [];
  let from = 0;

  for (const heading of headings) {
    const wanted = heading.trim();
    const index = found.findIndex(
      (candidate, at) => at >= from && candidate.text.trim() === wanted,
    );
    if (index === -1) return null;
    ranges.push({ start: found[index]!.start, end: body.length });
    from = index + 1;
  }

  /*
   * A section ends where the next PLANNED one begins, not at the next heading of
   * any kind. The model writes H3s inside a section, and those belong to it: a
   * redraw of 「ダウンタイムの実際」 has to replace its sub-headings too, or the
   * old ones are left orphaned under the new text.
   */
  return ranges.map((range, index) => ({
    start: range.start,
    end: ranges[index + 1]?.start ?? body.length,
  }));
}

/**
 * Replace one section's text, keeping everything around it.
 *
 * The replacement is expected to carry its own heading - which is what the
 * section drafting prompt asks the model for - so the whole range goes, heading
 * included. Blank lines are normalised at the seam, because a heading welded to
 * the paragraph above it renders as text rather than as a heading.
 */
export function replaceRange(body: string, range: SectionRange, text: string): string {
  const before = body.slice(0, range.start).replace(/\s+$/, '');
  const after = body.slice(range.end).replace(/^\s+/, '');
  const middle = text.trim();
  return [before, middle, after].filter((part) => part !== '').join('\n\n');
}

/**
 * Characters in one section, heading line included - the ones a reader sees.
 *
 * THE SAME ARTICLE WAS REPORTING TWO LENGTHS. This counted raw Markdown while
 * the writing screen and the final screen counted visible text, so an article
 * whose sections had tables read as 9,224 characters against a 4,700 target on
 * one screen and 4,431 on the next. Nearly six thousand of that was
 * `<table style="min-width: 100px;"><colgroup>…` - stored markup with about a
 * thousand characters of actual text inside it.
 *
 * The harm was not the disagreement. Every section was marked 長い against a
 * target it was in fact meeting, so the rail was telling a writer to cut
 * paragraphs that were the right length - and the one honest way to satisfy it
 * would have been to delete the tables.
 *
 * Markdown's own syntax is deliberately still counted: `##` and `|` are a
 * handful of characters and were always part of the number. What is removed is
 * the HTML, which is the part that arrives in volume. See shared/rich-format.ts.
 */
export const sectionLength = (body: string, range: SectionRange): number =>
  visibleLength(body.slice(range.start, range.end).trim());
